import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Shared GLSL. The Bayer dither is what keeps the hard toon bands from looking
// like cheap posterisation — it breaks the terminator into a stipple, which
// reads as deliberate pixel art rather than a banding artefact.
// ---------------------------------------------------------------------------

export const DITHER_GLSL = /* glsl */ `
const float BAYER[16] = float[16](
     0.0,  8.0,  2.0, 10.0,
    12.0,  4.0, 14.0,  6.0,
     3.0, 11.0,  1.0,  9.0,
    15.0,  7.0, 13.0,  5.0
);

float bayer(vec2 fragCoord) {
    int x = int(mod(fragCoord.x, 4.0));
    int y = int(mod(fragCoord.y, 4.0));
    return BAYER[y * 4 + x] / 16.0 - 0.5;
}

// Quantise into N bands, dithering across each band boundary.
float bandDither(float v, float steps, vec2 fragCoord, float strength) {
    float d = bayer(fragCoord) * strength / steps;
    return clamp(floor((v + d) * steps + 0.5) / steps, 0.0, 1.0);
}
`;

const VOXEL_VERT = /* glsl */ `
attribute vec2 aExtra;   // x = baked AO, y = emissive strength

varying vec3 vNormalW;
varying vec3 vColor;
varying float vAO;
varying float vEmissive;
varying float vDepth;
varying vec3 vWorldPos;

void main() {
    vColor = color;
    vAO = aExtra.x;
    vEmissive = aExtra.y;

    // Instancing is not free in a custom shader. three.js supplies the
    // instanceMatrix attribute and defines USE_INSTANCING, but nothing applies
    // it for you — leave this out and every instance draws at the mesh's own
    // origin, stacked on top of each other. Which is what eighteen clouds
    // stacked at sea level looked like.
    mat4 model = modelMatrix;
    #ifdef USE_INSTANCING
        model = model * instanceMatrix;
    #endif

    vNormalW = normalize(mat3(model) * normal);

    vec4 world = model * vec4(position, 1.0);
    vWorldPos = world.xyz;

    vec4 viewPos = viewMatrix * world;
    vDepth = -viewPos.z;
    gl_Position = projectionMatrix * viewPos;
}
`;

const VOXEL_FRAG = /* glsl */ `
#define MAX_LIGHTS 24

uniform vec3 uMoonDir;
uniform vec3 uMoonColor;
uniform vec3 uLightPos[MAX_LIGHTS];
uniform vec3 uLightColor[MAX_LIGHTS];
uniform float uLightRange[MAX_LIGHTS];
uniform int uLightCount;

uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform vec3 uSkyColor;
uniform vec3 uAmbientColor;
uniform vec3 uFogColor;
uniform vec2 uFogRange;
uniform float uNight;
uniform float uTime;
uniform vec3 uHighlight;
uniform float uHighlightAmount;

varying vec3 vNormalW;
varying vec3 vColor;
varying float vAO;
varying float vEmissive;
varying float vDepth;
varying vec3 vWorldPos;

${DITHER_GLSL}

void main() {
    vec3 N = normalize(vNormalW);

    // --- key light: hard toon bands, softened by dithering ---------------
    float ndl = dot(N, normalize(uSunDir));
    float wrapped = clamp(ndl * 0.5 + 0.5, 0.0, 1.0);   // wrap-around lambert
    float key = bandDither(wrapped, 4.0, gl_FragCoord.xy, 0.9);
    key = smoothstep(0.18, 1.0, key);

    // --- moon: the same banded treatment, cooler and much weaker ---------
    float ndm = dot(N, normalize(uMoonDir));
    float moonWrap = clamp(ndm * 0.5 + 0.5, 0.0, 1.0);
    float moonKey = bandDither(moonWrap, 3.0, gl_FragCoord.xy, 0.9);
    moonKey = smoothstep(0.25, 1.0, moonKey);

    // --- fill: hemisphere ambient from sky above, ground bounce below ----
    // Weighted toward the ambient term rather than raw sky, or every shaded
    // face turns blue and the green paint reads as black.
    float up = N.y * 0.5 + 0.5;
    vec3 fill = mix(uAmbientColor * 0.95, uSkyColor * 0.75 + uAmbientColor * 0.5, up);

    // Faces pointing north/south get a touch less light than east/west, which
    // separates the cube faces even where the sun does not.
    float facing = 1.0 - abs(N.z) * 0.06 - (1.0 - abs(N.y)) * 0.04;

    vec3 keyLight = uSunColor * key * 1.15 * (1.0 - uNight * 0.92)
                  + uMoonColor * moonKey * 0.95 * uNight;

    vec3 lit = vColor * (fill + keyLight) * vAO * facing;

    // --- windows and lanterns come on after sunset -----------------------
    float flicker = 0.94 + 0.06 * sin(uTime * 2.7 + vWorldPos.x * 3.1 + vWorldPos.z * 1.7);
    lit += vColor * vEmissive * uNight * 2.4 * flicker;

    // --- practical lights spilling out of those windows ------------------
    // Only after dark, so daylight costs nothing but the loop.
    if (uNight > 0.01) {
        vec3 spill = vec3(0.0);
        for (int i = 0; i < MAX_LIGHTS; i++) {
            if (i >= uLightCount) break;
            vec3 toLight = uLightPos[i] - vWorldPos;
            float dist = length(toLight);
            float range = uLightRange[i];
            if (dist > range) continue;

            // Soft falloff, then a wrapped lambert so faces turned away from
            // a lamp still catch some of it rather than going pure black.
            float atten = 1.0 - dist / range;
            atten *= atten;
            float lambert = clamp(dot(N, toLight / max(dist, 0.001)) * 0.6 + 0.4, 0.0, 1.0);

            // Lanterns gutter a little.
            float gutter = 0.9 + 0.1 * sin(uTime * 3.3 + float(i) * 2.4);
            spill += uLightColor[i] * atten * lambert * gutter;
        }
        lit += vColor * spill * uNight * 0.95;
    }

    // --- hover highlight --------------------------------------------------
    // Multiplicative, so a hovered mill brightens relative to its own colour
    // instead of being flooded toward white — which at night turned the whole
    // mill into a grey silhouette.
    lit *= 1.0 + uHighlightAmount * 1.9;
    lit += uHighlight * uHighlightAmount * 0.22 * (0.4 + 0.6 * key);

    // --- aerial perspective ----------------------------------------------
    float fog = smoothstep(uFogRange.x, uFogRange.y, vDepth);
    lit = mix(lit, uFogColor, fog * 0.92);

    gl_FragColor = vec4(lit, 1.0);
}
`;

/**
 * The material every voxel mesh uses. All instances share the environment's
 * uniform objects, so the day/night cycle updates them once for the whole world.
 */
export function createVoxelMaterial(env, { highlight = 0x000000 } = {}) {
    const material = new THREE.ShaderMaterial({
        vertexShader: VOXEL_VERT,
        fragmentShader: VOXEL_FRAG,
        vertexColors: true,
        uniforms: {
            uSunDir: env.uniforms.uSunDir,
            uSunColor: env.uniforms.uSunColor,
            uSkyColor: env.uniforms.uSkyColor,
            uAmbientColor: env.uniforms.uAmbientColor,
            uFogColor: env.uniforms.uFogColor,
            uFogRange: env.uniforms.uFogRange,
            uNight: env.uniforms.uNight,
            uTime: env.uniforms.uTime,
            uMoonDir: env.uniforms.uMoonDir,
            uMoonColor: env.uniforms.uMoonColor,
            uLightPos: env.uniforms.uLightPos,
            uLightColor: env.uniforms.uLightColor,
            uLightRange: env.uniforms.uLightRange,
            uLightCount: env.uniforms.uLightCount,
            // Per-material, not shared: each mill highlights on its own.
            uHighlight: { value: new THREE.Color(highlight).convertSRGBToLinear() },
            uHighlightAmount: { value: 0 },
        },
    });
    material.name = 'voxel';
    return material;
}

// ---------------------------------------------------------------------------
// Pick volumes: the invisible shapes the pointer actually hits, since sails,
// splashes and rigging are hopeless raycast targets.
//
// `colorWrite: false` is not enough — the mesh still goes through every render
// pass and can leave a silhouette behind, which is how an invisible box ends
// up visible. `visible: false` takes it out of rendering altogether, and the
// raycaster looks at the object rather than the material, so it still hits.
// ---------------------------------------------------------------------------
export const PICK_MATERIAL = new THREE.MeshBasicMaterial({
    visible: false,
    colorWrite: false,
    depthWrite: false,
});
